import React from 'react';
import { v4 } from 'uuid';
import { Button } from '../../../../../components/Button';
import { DropdownSearchSelect, DropdownSelect } from '../../../../../components/Dropdown';
import { TextInput } from '../../../../../components/TextInput';
import { DeleteIconButton } from '../../../../../icons';
import { PdfDataKey } from '../../../data/PdfDataKey';
import { PdfFieldResolving } from '../../../data/PdfFieldResolving';
import { PdfTableColumn, PdfTableComponent, PdfTextAlign, PdfOverflow } from '../../../PdfComponent';
import { usePdfTemplateContext } from '../../../PdfTemplateEditorContext';

export default function PdfTemplateTableColumns<T>({ component }: { component: PdfTableComponent }) {
  const { updateComponent, tableProviders, data } = usePdfTemplateContext<T>();
  const addColumn = () => {
    const cols = component.columns;
    const add: PdfTableColumn = {
      width: 20,
      id: v4(),
      key: tableProviders[0].columns[0],
      label: PdfFieldResolving.getLabel(tableProviders[0].columns[0], data),
      weight: 1,
      align: PdfTextAlign.left,
      overflow: PdfOverflow.ellipsis,
    };
    updateComponent(component.id, { columns: [...cols, add] });
  };

  const updateColumn = (colId: string, updates: Partial<PdfTableColumn>) => {
    const next = component.columns.map(c => (c.id === colId ? { ...c, ...updates } : c));
    updateComponent(component.id, { columns: next });
  };

  const removeColumn = (colId: string) => {
    const next = component.columns.filter(c => c.id !== colId);
    updateComponent(component.id, { columns: next });
  };

  const tableDataProvider = tableProviders.find(tp => tp.tableType === component.tableType);
  if (!tableDataProvider) return null;

  return (
    <div className='mt-2 grid gap-2'>
      <div className='flex items-center justify-between'>
        <p className='section-title'>Columns</p>
        <Button text={'+ Add column'} onClick={addColumn} />
      </div>

      <div className='grid gap-2'>
        {component.columns.map(col => (
          <div key={col.id} className='grid grid-cols-12 items-end gap-2 border-b border-gray-default pb-2'>
            <div className='col-span-6'>
              <TextInput label={'Label'} value={col.label} onChange={v => updateColumn(col.id, { label: v })} />
            </div>

            <div className='col-span-6'>
              <DropdownSearchSelect<PdfDataKey<any>>
                placeholder={'Select data key'}
                label={'Data key'}
                items={tableDataProvider.columns}
                textBuilder={key => PdfFieldResolving.getLabel(key, data)}
                onSelect={key => updateColumn(col.id, { key })}
                value={col.key}
              />
            </div>

            <div className='col-span-2'>
              <TextInput
                type='number'
                label={'Width'}
                value={col.width}
                onChange={v => {
                  updateColumn(col.id, { width: Number(v) });
                }}
              />
            </div>

            <div className='col-span-4'>
              <DropdownSelect<PdfTextAlign>
                label={'Align'}
                value={col.align}
                textBuilder={s => s}
                items={Object.values(PdfTextAlign)}
                onSelect={val => updateColumn(col.id, { align: val })}
              />
            </div>

            <div className='col-span-4'>
              <DropdownSelect<PdfOverflow>
                label={'Overflow'}
                value={col.overflow}
                textBuilder={s => s}
                items={Object.values(PdfOverflow)}
                onSelect={val => updateColumn(col.id, { overflow: val })}
              />
            </div>

            <div className='col-span-2 flex justify-end'>
              <DeleteIconButton onClick={() => removeColumn(col.id)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
